import { useEffect, useState, type ReactNode } from 'react';
import { AlertTriangle, FileText, Loader2, Pencil } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { useNotifications } from '../../store/useStore';
import { ErroreFattura, eventiService } from '../../lib/eventi/eventiService';
import { messaggioErrore } from '../../lib/eventi/errori';
import { formatCurrency } from '../../lib/subscriptionUtils';
import type { AnteprimaFattura, BigliettoEvento } from '../../lib/eventi/tipi';
import type { ClienteInserito } from '@eventi/datiCliente.ts';
import { ModuloClienteFattura } from './ModuloClienteFattura';

type Props = {
  ordine: BigliettoEvento['ordine'];
  aperta: boolean;
  onChiudi: () => void;
  /** Dopo un tentativo (riuscito o no): chi mostra l'ordine rilegge lo stato della fattura. */
  onCambiata: () => Promise<void>;
};

const giornoIt = (giorno: string | null) => (giorno ? giorno.split('-').reverse().join('/') : '—');

function descriviScheda(scheda: AnteprimaFattura['scheda']): string {
  if (!scheda) return '';
  if (scheda.come === 'esistente') return `Scheda in Clienti: ${scheda.nome}.`;
  if (scheda.come === 'prospect') return `Completa la scheda «${scheda.nome}» nata dall'ordine.`;
  return 'Nuova scheda in Clienti.';
}

function Sezione({ titolo, azione, children }: { titolo: string; azione?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-gray-200 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{titolo}</h4>
        {azione}
      </div>
      {children}
    </section>
  );
}

function Riepilogo({ a, onModifica }: { a: AnteprimaFattura; onModifica: (() => void) | null }) {
  const c = a.cliente;
  return (
    <div className="space-y-3 text-sm">
      {c && (
        <Sezione
          titolo="Cliente"
          azione={
            onModifica && (
              <button
                type="button"
                onClick={onModifica}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary-700 hover:bg-primary-50"
              >
                <Pencil className="h-3.5 w-3.5" /> Modifica dati
              </button>
            )
          }
        >
          <p className="font-medium text-gray-900">{c.denominazione}</p>
          <p className="text-xs text-neutral-600">
            {[c.partitaIva && `P.IVA ${c.partitaIva}`, c.codiceFiscale && c.codiceFiscale !== c.partitaIva && `CF ${c.codiceFiscale}`]
              .filter(Boolean)
              .join(' · ')}
          </p>
          <p className="text-xs text-neutral-600">
            {c.indirizzo}, {c.cap} {c.citta} ({c.provincia})
          </p>
          <p className="text-xs text-neutral-600">
            {c.codiceSdi ? `Codice SDI ${c.codiceSdi}` : c.pec ? `PEC ${c.pec}` : 'Senza codice SDI né PEC: arriva nel cassetto fiscale'}
          </p>
          <p className="mt-1 text-xs text-neutral-500">{descriviScheda(a.scheda)}</p>
        </Sezione>
      )}

      <Sezione titolo="Righe">
        <ul className="divide-y divide-gray-100">
          {a.righe.map((r, i) => (
            <li key={`${i}-${r.descrizione}`} className="flex justify-between gap-3 py-1.5">
              <span className="min-w-0">
                {r.quantita} × {r.descrizione}
              </span>
              <span className="whitespace-nowrap text-neutral-600">
                {formatCurrency(r.prezzo_unitario)} + IVA {r.aliquota_iva}%
              </span>
            </li>
          ))}
        </ul>
        {a.totali && (
          <dl className="mt-2 grid grid-cols-3 gap-2 rounded-md bg-gray-50 px-3 py-2 text-xs">
            <div>
              <dt className="text-neutral-500">Imponibile</dt>
              <dd className="font-medium">{formatCurrency(a.totali.imponibile)}</dd>
            </div>
            <div>
              <dt className="text-neutral-500">IVA</dt>
              <dd className="font-medium">{formatCurrency(a.totali.imposta)}</dd>
            </div>
            <div>
              <dt className="text-neutral-500">Totale</dt>
              <dd className="font-semibold text-gray-900">{formatCurrency(a.totali.totale)}</dd>
            </div>
          </dl>
        )}
      </Sezione>

      <Sezione titolo="Pagamento">
        <p className="text-xs text-neutral-700">
          Già pagata il {giornoIt(a.pagamento.il)} con carta ({a.pagamento.circuito})
          {a.pagamento.conto ? `: incasso sul conto «${a.pagamento.conto}» di Fatture in Cloud.` : '.'}
        </p>
        <p className="mt-1 text-xs text-neutral-500">Note: {a.note}</p>
      </Sezione>
    </div>
  );
}

/**
 * La finestra della fattura di un ordine del sito. Prima si vede cosa
 * nascera' (cliente, righe, importi, pagamento); se mancano dati del
 * cliente si completano qui e finiscono in Clienti. Solo "Crea su Fatture in
 * Cloud" crea la fattura.
 */
export function DialogFatturaOrdine({ ordine, aperta, onChiudi, onCambiata }: Props) {
  const { addNotification } = useNotifications();
  const [anteprima, setAnteprima] = useState<AnteprimaFattura | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState<'anteprima' | 'salva' | 'crea' | null>(null);
  const [modifica, setModifica] = useState(false);

  const mostra = (a: AnteprimaFattura) => {
    setAnteprima(a);
    // Dati del cliente da completare: il modulo si apre da solo.
    setModifica(Boolean(a.errori && a.bozza));
  };

  useEffect(() => {
    if (!aperta) return;
    let attiva = true;
    setAnteprima(null);
    setErrore(null);
    setModifica(false);
    setInCorso('anteprima');
    eventiService
      .anteprimaFattura(ordine.id)
      .then((a) => attiva && mostra(a))
      .catch((e) => attiva && setErrore(messaggioErrore(e)))
      .finally(() => attiva && setInCorso(null));
    return () => {
      attiva = false;
    };
  }, [aperta, ordine.id]);

  const salvaCliente = async (cliente: ClienteInserito) => {
    setInCorso('salva');
    setErrore(null);
    try {
      const a = await eventiService.salvaClienteFattura(ordine.id, cliente);
      mostra(a);
      addNotification({ type: 'success', title: 'Dati del cliente salvati in Clienti', message: a.cliente?.denominazione ?? '' });
    } catch (e) {
      setErrore(e instanceof ErroreFattura ? e.message : messaggioErrore(e));
    } finally {
      setInCorso(null);
    }
  };

  const crea = async () => {
    setInCorso('crea');
    setErrore(null);
    try {
      const fatta = await eventiService.creaFattura(ordine.id);
      addNotification({
        type: fatta.avvisi.length > 0 ? 'warning' : 'success',
        title: `Fattura ${fatta.numero ?? ''} creata su Fatture in Cloud`,
        message: [...fatta.avvisi, 'Controllala e inviala allo SDI da Fatture in Cloud.'].join(' '),
      });
      onChiudi();
    } catch (e) {
      // Dopo un tentativo andato male non si ripropone il pulsante: si riapre
      // la finestra e il server ricontrolla tutto (anche se e' gia' in creazione).
      setAnteprima(null);
      setErrore(messaggioErrore(e));
    } finally {
      setInCorso(null);
      await onCambiata();
    }
  };

  const pronta = anteprima?.fatturabile === true && !modifica;
  const conModulo = Boolean(anteprima?.bozza && modifica);

  return (
    <Dialog open={aperta} onOpenChange={(o) => !o && inCorso !== 'crea' && onChiudi()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Fattura dell'ordine n. {ordine.numero}
          </DialogTitle>
          <DialogDescription>
            Nasce su Fatture in Cloud con la data di oggi, già pagata. Non parte per lo SDI: la invii tu da Fatture in Cloud dopo
            averla controllata.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {inCorso === 'anteprima' && (
            <p className="flex items-center gap-2 py-6 text-sm text-neutral-600">
              <Loader2 className="h-4 w-4 animate-spin" /> Ricontrollo l'ordine sul sito e preparo la fattura…
            </p>
          )}

          {anteprima && !anteprima.fatturabile && (
            <div
              className={
                anteprima.bozza
                  ? 'flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800'
                  : 'flex gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700'
              }
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div>
                <p>{anteprima.motivo}</p>
                {anteprima.conti && anteprima.conti.length > 0 && (
                  <p className="mt-1 text-xs">Conti di saldo su Fatture in Cloud: {anteprima.conti.join(', ')}.</p>
                )}
              </div>
            </div>
          )}

          {conModulo && anteprima?.bozza && (
            <ModuloClienteFattura
              key={JSON.stringify(anteprima.bozza)}
              iniziale={anteprima.bozza}
              erroriIniziali={anteprima.errori}
              fonte={anteprima.fonte}
              inCorso={inCorso === 'salva'}
              onSalva={(c) => void salvaCliente(c)}
              onAnnulla={anteprima.fatturabile ? () => setModifica(false) : undefined}
            />
          )}

          {pronta && anteprima && <Riepilogo a={anteprima} onModifica={anteprima.bozza ? () => setModifica(true) : null} />}

          {anteprima && anteprima.avvisi.length > 0 && !conModulo && (
            <ul className="space-y-1 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              {anteprima.avvisi.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          )}

          {errore && (
            <div className="flex gap-3 whitespace-pre-line rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <p>{errore}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onChiudi} disabled={inCorso === 'crea'}>
            {pronta ? 'Annulla' : 'Chiudi'}
          </Button>
          {pronta && (
            <Button onClick={() => void crea()} disabled={inCorso !== null}>
              {inCorso === 'crea' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
              Crea su Fatture in Cloud
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
